import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import axios from 'axios';

// Registrar componentes do ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Endereço do serviço Python de monitoramento
const CACHE_SERVICE_URL = 'http://localhost:5500';

export const GlobalCacheReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  useEffect(() => {
    const fetchGlobalReport = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${CACHE_SERVICE_URL}/global-report`);
        if (response.data && response.data.status === 'success') {
          setReportData(response.data);
          setError(null);
        } else {
          throw new Error('Dados inválidos recebidos do serviço');
        }
      } catch (err) {
        console.error('Erro ao carregar relatório global:', err);
        setError(err.message || 'Erro ao carregar relatório global');
      } finally {
        setLoading(false);
      }
    };

    fetchGlobalReport();

    // Atualizar a cada 30 minutos
    const intervalId = setInterval(fetchGlobalReport, 1800000);
    
    return () => clearInterval(intervalId);
  }, [selectedPeriod]);

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch (e) {
      return 'Data inválida';
    }
  };

  // Renderizar gráfico de distribuição de taxa de acerto
  const renderHitRateDistributionChart = () => {
    if (!reportData || !reportData.salonStats || reportData.salonStats.length === 0) {
      return <div className="text-center p-4 text-gray-500">Dados insuficientes para gerar o gráfico</div>;
    }

    // Criar faixas de taxa de acerto
    const hitRateBuckets = {
      '90-100%': 0,
      '80-90%': 0,
      '70-80%': 0,
      '60-70%': 0,
      '<60%': 0
    };

    // Contabilizar salões em cada faixa
    reportData.salonStats.forEach(salon => {
      const hitRate = salon.hitRate * 100;
      if (hitRate >= 90) hitRateBuckets['90-100%']++;
      else if (hitRate >= 80) hitRateBuckets['80-90%']++;
      else if (hitRate >= 70) hitRateBuckets['70-80%']++;
      else if (hitRate >= 60) hitRateBuckets['60-70%']++;
      else hitRateBuckets['<60%']++;
    });

    const data = {
      labels: Object.keys(hitRateBuckets),
      datasets: [
        {
          label: 'Salões por Faixa de Taxa de Acerto',
          data: Object.values(hitRateBuckets),
          backgroundColor: [
            'rgba(75, 192, 192, 0.7)',
            'rgba(54, 162, 235, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            'rgba(255, 159, 64, 0.7)',
            'rgba(255, 99, 132, 0.7)',
          ],
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'right',
        },
        title: {
          display: true,
          text: 'Distribuição de Salões por Taxa de Acerto',
        },
      },
    };

    return <Pie data={data} options={options} />;
  };

  // Renderizar gráfico dos top salões
  const renderTopSalonsChart = () => {
    if (!reportData || !reportData.salonStats || reportData.salonStats.length === 0) {
      return <div className="text-center p-4 text-gray-500">Dados insuficientes para gerar o gráfico</div>;
    }

    // Pegar os 10 melhores salões por taxa de acerto
    const topSalons = [...reportData.salonStats]
      .sort((a, b) => b.hitRate - a.hitRate)
      .slice(0, 10);

    const data = {
      labels: topSalons.map(salon => salon.name || `Salão ${salon.id}`),
      datasets: [
        {
          label: 'Taxa de Acerto (%)',
          data: topSalons.map(salon => (salon.hitRate * 100).toFixed(1)),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
        }
      ]
    };

    const options = {
      indexAxis: 'y',
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Top 10 Salões por Taxa de Acerto',
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Taxa de Acerto (%)'
          }
        }
      }
    };

    return <Bar data={data} options={options} />;
  };

  // Exibir estado de carregamento
  if (loading) {
    return (
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-4">Relatório Global de Cache</h3>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
          <span className="ml-2">Carregando relatório global...</span>
        </div>
      </div>
    );
  }

  // Exibir erro
  if (error) {
    return (
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-4">Relatório Global de Cache</h3>
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
          <p className="font-bold">Erro ao carregar relatório global</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Se não há dados
  if (!reportData || !reportData.globalStats) {
    return (
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-4">Relatório Global de Cache</h3>
        <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4">
          <p className="font-bold">Sem dados disponíveis</p>
          <p>Não há dados de cache globais disponíveis no momento.</p>
        </div>
      </div>
    );
  }

  // Extrair métricas globais
  const { globalStats, salonStats } = reportData;
  const totalSalons = salonStats?.length || globalStats.totalSalons || 0;
  const globalHitRate = globalStats.globalHitRate || 0;
  const totalRequests = globalStats.totalRequests || 0;
  const totalHits = globalStats.totalHits || 0;
  const totalMisses = globalStats.totalMisses || 0;
  
  // Calcular economia estimada
  // Preço médio de Firestore: $0.036 por 100.000 leituras
  const firestoreCostPer100K = 0.036;
  const estimatedSavings = (totalHits * firestoreCostPer100K) / 100000;

  return (
    <div className="bg-white rounded-lg">
      <h3 className="text-lg font-semibold mb-4">Relatório Global de Cache SaaS</h3>
      
      {/* Seleção de período */}
      <div className="flex mb-6">
        <button 
          onClick={() => setSelectedPeriod('day')}
          className={`px-4 py-2 ${selectedPeriod === 'day' ? 'bg-blue-500 text-white' : 'bg-gray-200'} rounded-l`}>
          Dia
        </button>
        <button 
          onClick={() => setSelectedPeriod('week')}
          className={`px-4 py-2 ${selectedPeriod === 'week' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
          Semana
        </button>
        <button 
          onClick={() => setSelectedPeriod('month')}
          className={`px-4 py-2 ${selectedPeriod === 'month' ? 'bg-blue-500 text-white' : 'bg-gray-200'} rounded-r`}>
          Mês
        </button>
      </div>
      
      {/* Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-600">Total de Salões</p>
          <p className="text-3xl font-bold">{totalSalons}</p>
          <p className="text-xs text-blue-500">Salões monitorados</p>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-green-600">Taxa de Acerto Média</p>
          <p className="text-3xl font-bold">{(globalHitRate * 100).toFixed(1)}%</p>
          <p className="text-xs text-green-500">Média global</p>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <p className="text-sm text-purple-600">Requisições Economizadas</p>
          <p className="text-3xl font-bold">{totalHits.toLocaleString('pt-BR')}</p>
          <p className="text-xs text-purple-500">De {totalRequests.toLocaleString('pt-BR')} totais</p>
        </div>
        
        <div className="bg-amber-50 p-4 rounded-lg">
          <p className="text-sm text-amber-600">Economia Estimada</p>
          <p className="text-3xl font-bold">R$ {estimatedSavings.toFixed(2)}</p>
          <p className="text-xs text-amber-500">Baseado no custo do Firestore</p>
        </div>
      </div>
      
      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <h4 className="text-md font-medium mb-2">Distribuição de Taxa de Acerto</h4>
          {renderHitRateDistributionChart()}
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <h4 className="text-md font-medium mb-2">Top Salões por Desempenho</h4>
          {renderTopSalonsChart()}
        </div>
      </div>
      
      {/* Lista de salões */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h4 className="text-md font-medium mb-2">Desempenho por Salão</h4>
        <div className="max-h-96 overflow-y-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salão</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taxa de Acerto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requisições</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Economia Est.</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {salonStats && salonStats.map((salon, index) => {
                const salonSavings = (salon.hits * firestoreCostPer100K) / 100000;
                return (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="px-6 py-2 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{salon.name || `Salão ${salon.id}`}</div>
                          <div className="text-xs text-gray-500">{salon.email || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full
                        ${salon.hitRate >= 0.9 ? 'bg-green-100 text-green-800' : 
                          salon.hitRate >= 0.7 ? 'bg-blue-100 text-blue-800' :
                          salon.hitRate >= 0.5 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'}`}>
                        {(salon.hitRate * 100).toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                      {salon.requests.toLocaleString('pt-BR')}
                      <span className="text-xs ml-1">
                        ({salon.hits.toLocaleString('pt-BR')} / {salon.misses.toLocaleString('pt-BR')})
                      </span>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                      R$ {salonSavings.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Última atualização */}
      <div className="text-right text-xs text-gray-500">
        Última atualização: {new Date(reportData.timestamp).toLocaleString('pt-BR')}
      </div>
    </div>
  );
};

export default GlobalCacheReport; 